'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { PendingReduction } from '@flipster/apis/v1/earn/types';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import InProgressIcon from '@flipster/react/icon/generated/InProgressIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';

export const PendingReductions = ({ currency }: { currency: string }) => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const { pendingReductions } = getEarnSummaryOfCurrency(currency, earnSummary);

  return (
    <details
      className={stack({
        borderTopWidth: '1px',
        borderTopStyle: 'solid',
        borderColor: 'border.default.level-1',
        py: 12,
        cursor: 'pointer',
        userSelect: 'none',
      })}
    >
      <summary>
        <div className={hstack({ justify: 'space-between' })}>
          <div className={stack()}>
            <Badge type='outline' color='accent' leadingIcon={<InProgressIcon size={12} />}>
              <FormattedMessage id='Earn.r260408.Redemptions_in_progress' values={{ n: pendingReductions.length }} />
            </Badge>
          </div>
          <div className={hstack({ gap: 4 })}>
            <span
              className={square({
                size: 24,
                color: 'content.default.level-3',
                '[open] &': { transform: 'rotate(-180deg)' },
              })}
            >
              <ChevronDownIcon size={16} />
            </span>
          </div>
        </div>
      </summary>
      <div>
        {pendingReductions.map((reduction: PendingReduction) => (
          <div
            className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5', mt: 12 })}
            key={reduction.scheduledAt}
          >
            <Formatted.Number
              value={reduction.amount}
              suffix={UsdtCurrency}
              precision={UsdtPrecision}
              toFixed
              css={{ display: 'inline' }}
            />
            <p className={css({ color: 'content.default.level-2', mt: 2 })}>
              <Datetime value={reduction.scheduledAt} format='MM/dd/yy HH:mm:ss' isUTC withUTCText />
            </p>
          </div>
        ))}
        <div
          className={css({
            borderTopWidth: '1px',
            borderTopStyle: 'solid',
            borderColor: 'border.default.level-1',
            mt: 12,
            pt: 8,
            textStyle: 'Text/S/Regular',
            color: 'content.default.level-2',
          })}
        >
          <FormattedMessage id='Earn.r260318.Pusdt_redemption_disclaimer' />
        </div>
      </div>
    </details>
  );
};
